import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von10Outcomes } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface VonSurgeryFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const VonSurgeryForm: React.FC<VonSurgeryFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  // Surgeries performed
  const [ropSurgery, setRopSurgery] = useState(false);
  const [pdaSurgery, setPdaSurgery] = useState(false);
  const [necSurgery, setNecSurgery] = useState(false);
  const [otherSurgery, setOtherSurgery] = useState(false);

  // Surgery details (10 procedures)
  const [surgeryCode1, setSurgeryCode1] = useState<string>('');
  const [surgeryLoc1, setSurgeryLoc1] = useState<string>('');
  const [surgerySsi1, setSurgerySsi1] = useState(false);

  const [surgeryCode2, setSurgeryCode2] = useState<string>('');
  const [surgeryLoc2, setSurgeryLoc2] = useState<string>('');
  const [surgerySsi2, setSurgerySsi2] = useState(false);

  const [surgeryCode3, setSurgeryCode3] = useState<string>('');
  const [surgeryLoc3, setSurgeryLoc3] = useState<string>('');
  const [surgerySsi3, setSurgerySsi3] = useState(false);

  const [surgeryCode4, setSurgeryCode4] = useState<string>('');
  const [surgeryLoc4, setSurgeryLoc4] = useState<string>('');
  const [surgerySsi4, setSurgerySsi4] = useState(false);

  const [surgeryCode5, setSurgeryCode5] = useState<string>('');
  const [surgeryLoc5, setSurgeryLoc5] = useState<string>('');
  const [surgerySsi5, setSurgerySsi5] = useState(false);

  const [surgeryCode6, setSurgeryCode6] = useState<string>('');
  const [surgeryLoc6, setSurgeryLoc6] = useState<string>('');
  const [surgerySsi6, setSurgerySsi6] = useState(false);

  const [surgeryCode7, setSurgeryCode7] = useState<string>('');
  const [surgeryLoc7, setSurgeryLoc7] = useState<string>('');
  const [surgerySsi7, setSurgerySsi7] = useState(false);

  const [surgeryCode8, setSurgeryCode8] = useState<string>('');
  const [surgeryLoc8, setSurgeryLoc8] = useState<string>('');
  const [surgerySsi8, setSurgerySsi8] = useState(false);

  const [surgeryCode9, setSurgeryCode9] = useState<string>('');
  const [surgeryLoc9, setSurgeryLoc9] = useState<string>('');
  const [surgerySsi9, setSurgerySsi9] = useState(false);

  const [surgeryCode10, setSurgeryCode10] = useState<string>('');
  const [surgeryLoc10, setSurgeryLoc10] = useState<string>('');
  const [surgerySsi10, setSurgerySsi10] = useState(false);

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getOutcomes(patientId);

      if (data) {
        setHasExistingData(true);
        setRopSurgery(Boolean(data.rop_surgery));
        setPdaSurgery(Boolean(data.pda_surgery));
        setNecSurgery(Boolean(data.nec_surgery));
        setOtherSurgery(Boolean(data.other_surgery));

        setSurgeryCode1(data.surgery_code_1 || '');
        setSurgeryLoc1(data.surgery_loc_1?.toString() || '');
        setSurgerySsi1(Boolean(data.surgery_ssi_1));

        setSurgeryCode2(data.surgery_code_2 || '');
        setSurgeryLoc2(data.surgery_loc_2?.toString() || '');
        setSurgerySsi2(Boolean(data.surgery_ssi_2));

        setSurgeryCode3(data.surgery_code_3 || '');
        setSurgeryLoc3(data.surgery_loc_3?.toString() || '');
        setSurgerySsi3(Boolean(data.surgery_ssi_3));

        setSurgeryCode4(data.surgery_code_4 || '');
        setSurgeryLoc4(data.surgery_loc_4?.toString() || '');
        setSurgerySsi4(Boolean(data.surgery_ssi_4));

        setSurgeryCode5(data.surgery_code_5 || '');
        setSurgeryLoc5(data.surgery_loc_5?.toString() || '');
        setSurgerySsi5(Boolean(data.surgery_ssi_5));

        setSurgeryCode6(data.surgery_code_6 || '');
        setSurgeryLoc6(data.surgery_loc_6?.toString() || '');
        setSurgerySsi6(Boolean(data.surgery_ssi_6));

        setSurgeryCode7(data.surgery_code_7 || '');
        setSurgeryLoc7(data.surgery_loc_7?.toString() || '');
        setSurgerySsi7(Boolean(data.surgery_ssi_7));

        setSurgeryCode8(data.surgery_code_8 || '');
        setSurgeryLoc8(data.surgery_loc_8?.toString() || '');
        setSurgerySsi8(Boolean(data.surgery_ssi_8));

        setSurgeryCode9(data.surgery_code_9 || '');
        setSurgeryLoc9(data.surgery_loc_9?.toString() || '');
        setSurgerySsi9(Boolean(data.surgery_ssi_9));

        setSurgeryCode10(data.surgery_code_10 || '');
        setSurgeryLoc10(data.surgery_loc_10?.toString() || '');
        setSurgerySsi10(Boolean(data.surgery_ssi_10));

      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von10Outcomes> = {
        patient_id: patientId,
        rop_surgery: ropSurgery,
        pda_surgery: pdaSurgery,
        nec_surgery: necSurgery,
        other_surgery: otherSurgery,

        surgery_code_1: surgeryCode1 || undefined,
        surgery_loc_1: surgeryLoc1 ? parseInt(surgeryLoc1) : undefined,
        surgery_ssi_1: surgerySsi1,

        surgery_code_2: surgeryCode2 || undefined,
        surgery_loc_2: surgeryLoc2 ? parseInt(surgeryLoc2) : undefined,
        surgery_ssi_2: surgerySsi2,

        surgery_code_3: surgeryCode3 || undefined,
        surgery_loc_3: surgeryLoc3 ? parseInt(surgeryLoc3) : undefined,
        surgery_ssi_3: surgerySsi3,

        surgery_code_4: surgeryCode4 || undefined,
        surgery_loc_4: surgeryLoc4 ? parseInt(surgeryLoc4) : undefined,
        surgery_ssi_4: surgerySsi4,

        surgery_code_5: surgeryCode5 || undefined,
        surgery_loc_5: surgeryLoc5 ? parseInt(surgeryLoc5) : undefined,
        surgery_ssi_5: surgerySsi5,

        surgery_code_6: surgeryCode6 || undefined,
        surgery_loc_6: surgeryLoc6 ? parseInt(surgeryLoc6) : undefined,
        surgery_ssi_6: surgerySsi6,

        surgery_code_7: surgeryCode7 || undefined,
        surgery_loc_7: surgeryLoc7 ? parseInt(surgeryLoc7) : undefined,
        surgery_ssi_7: surgerySsi7,

        surgery_code_8: surgeryCode8 || undefined,
        surgery_loc_8: surgeryLoc8 ? parseInt(surgeryLoc8) : undefined,
        surgery_ssi_8: surgerySsi8,

        surgery_code_9: surgeryCode9 || undefined,
        surgery_loc_9: surgeryLoc9 ? parseInt(surgeryLoc9) : undefined,
        surgery_ssi_9: surgerySsi9,

        surgery_code_10: surgeryCode10 || undefined,
        surgery_loc_10: surgeryLoc10 ? parseInt(surgeryLoc10) : undefined,
        surgery_ssi_10: surgerySsi10,

      };

      if (hasExistingData) {
        await vonDataService.updateOutcomes(patientId, formData);
      } else {
        await vonDataService.createOutcomes(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderSurgeryFields = (num: number, code: string, setCode: (v: string) => void, loc: string, setLoc: (v: string) => void, ssi: boolean, setSsi: (v: boolean) => void) => (
    <div key={num} className="p-4 border rounded-lg space-y-3">
      <h4 className="text-sm font-medium">Procedură {num}</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label htmlFor={`surgeryCode${num}`}>Cod chirurgie (RONN Appendix D)</Label>
          <Input
            id={`surgeryCode${num}`}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Cod RONN"
            disabled={readOnly}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`surgeryLoc${num}`}>Locație</Label>
          <Select value={loc} onValueChange={setLoc} disabled={readOnly}>
            <SelectTrigger id={`surgeryLoc${num}`}>
              <SelectValue placeholder="Selectează..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Spitalul dumneavoastră</SelectItem>
              <SelectItem value="2">Altul</SelectItem>
              <SelectItem value="3">Ambele</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-2 pt-8">
          <Checkbox
            id={`surgerySsi${num}`}
            checked={ssi}
            onCheckedChange={(checked) => setSsi(checked === true)}
            disabled={readOnly}
          />
          <Label htmlFor={`surgerySsi${num}`} className="text-sm font-normal cursor-pointer">
            Infecție locul intervenției
          </Label>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Intervenții chirurgicale</CardTitle>
        <CardDescription>Intervenții chirurgicale efectuate și detalii pentru până la 10 proceduri</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Surgeries performed */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Intervenții chirurgicale efectuate</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ropSurgery"
                  checked={ropSurgery}
                  onCheckedChange={(checked) => setRopSurgery(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ropSurgery" className="text-sm font-normal cursor-pointer">
                  Chirurgie ROP
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="pdaSurgery"
                  checked={pdaSurgery}
                  onCheckedChange={(checked) => setPdaSurgery(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="pdaSurgery" className="text-sm font-normal cursor-pointer">
                  Chirurgie/cateterism PDA
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="necSurgery"
                  checked={necSurgery}
                  onCheckedChange={(checked) => setNecSurgery(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="necSurgery" className="text-sm font-normal cursor-pointer">
                  Chirurgie pentru NEC/perforație
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="otherSurgery"
                  checked={otherSurgery}
                  onCheckedChange={(checked) => setOtherSurgery(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="otherSurgery" className="text-sm font-normal cursor-pointer">
                  Altă chirurgie
                </Label>
              </div>
            </div>
          </div>

          {/* Surgery details (up to 10 procedures) */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Detalii intervenții chirurgicale (până la 10 proceduri)</h3>
            <div className="space-y-3">
              {renderSurgeryFields(1, surgeryCode1, setSurgeryCode1, surgeryLoc1, setSurgeryLoc1, surgerySsi1, setSurgerySsi1)}
              {renderSurgeryFields(2, surgeryCode2, setSurgeryCode2, surgeryLoc2, setSurgeryLoc2, surgerySsi2, setSurgerySsi2)}
              {renderSurgeryFields(3, surgeryCode3, setSurgeryCode3, surgeryLoc3, setSurgeryLoc3, surgerySsi3, setSurgerySsi3)}
              {renderSurgeryFields(4, surgeryCode4, setSurgeryCode4, surgeryLoc4, setSurgeryLoc4, surgerySsi4, setSurgerySsi4)}
              {renderSurgeryFields(5, surgeryCode5, setSurgeryCode5, surgeryLoc5, setSurgeryLoc5, surgerySsi5, setSurgerySsi5)}
              {renderSurgeryFields(6, surgeryCode6, setSurgeryCode6, surgeryLoc6, setSurgeryLoc6, surgerySsi6, setSurgerySsi6)}
              {renderSurgeryFields(7, surgeryCode7, setSurgeryCode7, surgeryLoc7, setSurgeryLoc7, surgerySsi7, setSurgerySsi7)}
              {renderSurgeryFields(8, surgeryCode8, setSurgeryCode8, surgeryLoc8, setSurgeryLoc8, surgerySsi8, setSurgerySsi8)}
              {renderSurgeryFields(9, surgeryCode9, setSurgeryCode9, surgeryLoc9, setSurgeryLoc9, surgerySsi9, setSurgerySsi9)}
              {renderSurgeryFields(10, surgeryCode10, setSurgeryCode10, surgeryLoc10, setSurgeryLoc10, surgerySsi10, setSurgerySsi10)}
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
